import React from 'react';
import { DollarSign, TrendingUp, CreditCard, Landmark, PieChart } from 'lucide-react';

export default function ProfitabilityTables({ tables, loading = false }) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
        <div className="h-64 bg-gray-100 animate-pulse rounded-lg"></div>
      </div>
    );
  }

  if (!tables) {
    return null;
  }

  const { cc_based, ach_only, combined } = tables;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  const TableCard = ({ title, data, icon: Icon, iconColor, description }) => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-10 h-10 rounded-lg ${iconColor} bg-opacity-10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor.replace('bg-', 'text-')}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricBox label="Gross Revenue" value={formatCurrency(data.gross_revenue)} />
          <MetricBox label="Merchant Costs" value={formatCurrency(data.merchant_costs || data.merchant_costs_excl_ach)} />
          <MetricBox label="Net Revenue" value={formatCurrency(data.net_revenue)} />
          <MetricBox 
            label="Gross Profit Margin" 
            value={formatPercent(data.gpm)} 
            highlight={data.gpm > 70 ? 'green' : data.gpm > 40 ? 'blue' : 'amber'}
          />
          <MetricBox label="Implied Take Rate" value={formatPercent(data.take_rate)} />
          <MetricBox label="Avg Transaction" value={formatCurrency(data.avg_transaction)} />
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
          <div className="text-sm">
            <span className="text-slate-500">Total Collections:</span>
            <span className="ml-2 font-semibold text-slate-900">{formatCurrency(data.collections)}</span>
          </div>
          <div className="text-sm">
            <span className="text-slate-500">Total Transactions:</span>
            <span className="ml-2 font-semibold text-slate-900">{Math.round(data.transactions).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const MetricBox = ({ label, value, highlight }) => {
    const highlightColors = {
      green: 'bg-green-50 border-green-200 text-green-900',
      blue: 'bg-blue-50 border-blue-200 text-blue-900',
      amber: 'bg-amber-50 border-amber-200 text-amber-900',
    };

    return (
      <div className={`p-3 rounded-lg border ${highlight ? highlightColors[highlight] : 'bg-slate-50 border-slate-200'}`}>
        <div className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">{label}</div>
        <div className={`text-lg font-bold ${highlight ? '' : 'text-slate-900'}`}>{value}</div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Profitability Analysis</h2>
          <p className="text-sm text-slate-600 mt-1">Payment method breakdown with margin calculations</p>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Credit Card Based Payments */}
        <TableCard
          title="Credit Card Based Payments"
          description="CC + AMEX + Debit"
          data={cc_based}
          icon={CreditCard}
          iconColor="bg-purple-500"
        />

        {/* ACH Only */}
        <TableCard
          title="ACH Payments"
          description="Electronic bank transfers with ~80% margin"
          data={ach_only}
          icon={Landmark}
          iconColor="bg-blue-500"
        />

        {/* Combined */}
        <TableCard
          title="Combined Total"
          description="All revenue - ACH merchant fees excluded from margin"
          data={combined}
          icon={PieChart}
          iconColor="bg-emerald-500"
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="text-blue-600 mt-0.5">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">How to Read These Tables</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Credit Card Based:</strong> Shows profitability for card payments (lower margins due to processing fees)</li>
              <li>• <strong>ACH:</strong> Bank transfers have much higher margins (~80%) due to lower processing costs</li>
              <li>• <strong>Combined:</strong> Total revenue across all methods, with ACH costs excluded to show true operational margin</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
